import {
  RIGHTSIZED_AUTOSCALED_LABEL,
  SEGMENT_DISPLAY_LABELS,
  fetchApps,
  fetchDatabases,
  fetchManualScaleRecommendations,
  fetchServiceSizingPolicies,
  fetchServices,
} from "@app/deploy";
import type { SegmentState } from "@app/deploy";
import { selectOrganizationSelected } from "@app/organizations";
import { useCompositeLoader, useSelector } from "@app/react";
import {
  createDbUrl,
  databasesUrlWithSearch,
  deployUrl,
  servicesUrlWithSearch,
} from "@app/routes";
import {
  type ContainerEfficiencyItem,
  useAppServicesByOrgWithState,
  useDatabasesByOrgWithState,
} from "@app/ui/hooks";
import {
  IconBox,
  IconCylinder,
  Popover,
  SegmentedBar,
  type SegmentedBarSegment,
} from "@app/ui/shared";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  type ContainerEfficiencyVariant,
  HOME_DASH_EVENTS,
  type HomeDashOrg,
  homeDashEvent,
  homeDashOrgPayload,
} from "./tuna-events";

/** Minimum time to show skeleton to avoid flash when API is fast. */
const SKELETON_MIN_MS = 1800;
/** Delay after loader reports done before showing content (lets store settle). */
const SETTLE_MS = 250;
/** Bar colors: under-provisioned = red-500, over-provisioned = indigo-200 (theme tokens). */
const SEGMENT_BAR_FILL: Record<SegmentState, string> = {
  scale_down: "bg-indigo-200",
  scale_up: "bg-red-500",
  rightsized: "bg-gray-200",
  autoscaled: "bg-gray-200",
};

function segmentCounts(
  items: ContainerEfficiencyItem[],
): Record<SegmentState, number> {
  const counts: Record<SegmentState, number> = {
    scale_down: 0,
    scale_up: 0,
    rightsized: 0,
    autoscaled: 0,
  };
  for (const item of items) {
    counts[item.segmentState]++;
  }
  return counts;
}

type EfficiencyVariant = "app" | "database";
type EfficiencyBarState = "scale_up" | "scale_down" | "rightsized";

const EFFICIENCY_BAR_STATES: EfficiencyBarState[] = [
  "scale_up",
  "scale_down",
  "rightsized",
];

function getSegmentLabel(
  state: EfficiencyBarState,
  variant: EfficiencyVariant,
): string {
  if (state === "rightsized") {
    return variant === "app"
      ? RIGHTSIZED_AUTOSCALED_LABEL
      : SEGMENT_DISPLAY_LABELS.rightsized;
  }
  return SEGMENT_DISPLAY_LABELS[state];
}

type BarSegment = SegmentedBarSegment & { state: EfficiencyBarState };

function getSegmentDefinitions(
  variant: EfficiencyVariant,
): Omit<BarSegment, "pct">[] {
  return EFFICIENCY_BAR_STATES.map((state) => ({
    state,
    label: getSegmentLabel(state, variant),
    fill: SEGMENT_BAR_FILL[state],
  }));
}

function getBarSegments(
  items: ContainerEfficiencyItem[],
  variant: EfficiencyVariant,
): BarSegment[] {
  const total = items.length;
  const counts = segmentCounts(items);
  const over = total === 0 ? 0 : Math.round((counts.scale_down / total) * 100);
  const under = total === 0 ? 0 : Math.round((counts.scale_up / total) * 100);
  const pctByState: Record<EfficiencyBarState, number> = {
    scale_up: under,
    scale_down: over,
    rightsized: total === 0 ? 0 : 100 - over - under,
  };

  return getSegmentDefinitions(variant).map((segment) => ({
    ...segment,
    pct: pctByState[segment.state],
  }));
}

function getRecommendationTooltipData(items: ContainerEfficiencyItem[]): {
  under: { count: number };
  over: { count: number };
} {
  const counts = segmentCounts(items);
  return {
    under: { count: counts.scale_up },
    over: { count: counts.scale_down },
  };
}

/** Rounded integer at ≥1%; one decimal below 1%; "<0.1" when still 0.0 with count > 0. */
function formatEfficiencyTooltipPct(count: number, total: number): string {
  if (total === 0 || count === 0) {
    return "0";
  }
  const exact = (count / total) * 100;
  const rounded = Math.round(exact);
  if (rounded >= 1) {
    return String(rounded);
  }
  const oneDecimal = exact.toFixed(1);
  if (oneDecimal !== "0.0") {
    return oneDecimal;
  }
  return "<0.1";
}

function EfficiencyTooltipContent({
  variant,
  under,
  over,
  totalItems,
  org,
}: {
  variant: ContainerEfficiencyVariant;
  under: { count: number };
  over: { count: number };
  totalItems: number;
  org: HomeDashOrg;
}) {
  const title =
    variant === "database"
      ? "Database Container Efficiency"
      : "App Service Container Efficiency";
  const showLink = under.count > 0 || over.count > 0;
  const recommendationsLinkUrl =
    variant === "database"
      ? `${databasesUrlWithSearch("scalerec:scale*")}&sortBy=cost&sortDir=desc`
      : `${servicesUrlWithSearch("scalerec:scale*")}&sortBy=cost&sortDir=desc`;
  return (
    <div className="flex min-w-full flex-col gap-1.5">
      <div className="font-semibold text-gray-900 pb-0.5">{title}</div>
      {under.count > 0 && (
        <div className="flex gap-2">
          <span
            className={`inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full mt-0.5 ${SEGMENT_BAR_FILL.scale_up}`}
            aria-hidden
          />
          <div className="flex flex-col">
            <span>
              Under-provisioned:{" "}
              {formatEfficiencyTooltipPct(under.count, totalItems)}% (
              {under.count})
            </span>
          </div>
        </div>
      )}
      {over.count > 0 && (
        <div className="flex gap-2">
          <span
            className={`inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full mt-0.5 ${SEGMENT_BAR_FILL.scale_down}`}
            aria-hidden
          />
          <div className="flex flex-col">
            <span>
              Over-provisioned:{" "}
              {formatEfficiencyTooltipPct(over.count, totalItems)}% (
              {over.count})
            </span>
          </div>
        </div>
      )}
      {under.count === 0 && over.count === 0 && (
        <span>All containers are right-sized.</span>
      )}
      {showLink && (
        <div className="mt-1">
          <Link
            to={recommendationsLinkUrl}
            className="underline"
            onClick={() =>
              homeDashEvent(
                HOME_DASH_EVENTS.CONTAINER_EFFICIENCY_RECOMMENDATIONS_CLICK,
                {
                  ...homeDashOrgPayload(org),
                  variant,
                  underCount: under.count,
                  overCount: over.count,
                },
              )
            }
          >
            See Recommendations
          </Link>
        </div>
      )}
    </div>
  );
}

function SharedEfficiencyLegend() {
  const segments = getSegmentDefinitions("app");
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-700">
      {segments.map((seg) => (
        <span key={seg.label} className="flex items-center gap-1.5">
          <span
            className={`inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full ${seg.fill}`}
            aria-hidden
          />
          <span>{seg.label}</span>
        </span>
      ))}
    </div>
  );
}

export const Scaling = () => {
  const org = useSelector(selectOrganizationSelected);
  const loader = useCompositeLoader([
    fetchApps,
    fetchServices,
    fetchDatabases,
    fetchManualScaleRecommendations,
    fetchServiceSizingPolicies,
  ]);

  const dbByOrg = useDatabasesByOrgWithState();
  const appByOrg = useAppServicesByOrgWithState();
  const isLoading = loader.isInitialLoading;

  /** Preload/cache: first paint already has data — skip min-skeleton and settle delays. */
  const isWarmFirstPaintRef = useRef(!loader.isInitialLoading);
  const [skeletonMinReached, setSkeletonMinReached] = useState(
    () => isWarmFirstPaintRef.current,
  );
  const [contentSettled, setContentSettled] = useState(
    () => isWarmFirstPaintRef.current,
  );

  useEffect(() => {
    if (isWarmFirstPaintRef.current) return;
    const id = setTimeout(() => setSkeletonMinReached(true), SKELETON_MIN_MS);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (isWarmFirstPaintRef.current) {
      setContentSettled(true);
      return;
    }
    if (isLoading) {
      setContentSettled(false);
      return;
    }
    const id = setTimeout(() => setContentSettled(true), SETTLE_MS);
    return () => clearTimeout(id);
  }, [isLoading]);

  const showSkeleton =
    isLoading ||
    (!isWarmFirstPaintRef.current && (!skeletonMinReached || !contentSettled));

  const dbTooltipData = getRecommendationTooltipData(
    showSkeleton ? [] : dbByOrg,
  );
  const appTooltipData = getRecommendationTooltipData(
    showSkeleton ? [] : appByOrg,
  );

  const showSharedLegend =
    !showSkeleton && (appByOrg.length > 0 || dbByOrg.length > 0);

  return (
    <div className="flex flex-col h-full">
      <h2 className="text-lg font-medium mb-4">Container Efficiency</h2>
      <div className="bg-white rounded-lg border border-gray-200 shadow flex-1 p-4">
        <div
          className="flex flex-col gap-4"
          aria-busy={showSkeleton}
          aria-label={showSkeleton ? "Loading container efficiency" : undefined}
        >
          {showSkeleton ? (
            <div className="flex items-center gap-3 w-full">
              <div className="flex items-center gap-1.5 text-gray-600 w-36 flex-shrink-0">
                <IconBox variant="sm" className="flex-shrink-0" />
                <span className="text-sm font-medium">App Service</span>
              </div>
              <div className="flex-1 min-w-0 h-5 rounded bg-gray-200 animate-pulse" />
            </div>
          ) : appByOrg.length === 0 ? (
            <div className="flex items-center gap-3 w-full">
              <div className="flex items-center gap-1.5 text-gray-600 w-36 flex-shrink-0">
                <IconBox variant="sm" className="flex-shrink-0" />
                <span className="text-sm font-medium">App Service</span>
              </div>
              <div className="flex-1 min-w-0 text-sm text-gray-600">
                No apps.{" "}
                <Link
                  to={deployUrl()}
                  className="underline"
                  onClick={() =>
                    homeDashEvent(
                      HOME_DASH_EVENTS.CONTAINER_EFFICIENCY_EMPTY_STATE_CTA,
                      {
                        ...homeDashOrgPayload(org),
                        target: "deploy",
                      },
                    )
                  }
                >
                  Deploy an app →
                </Link>
              </div>
            </div>
          ) : (
            <Popover
              onOpenChange={(isOpen) => {
                if (!isOpen) return;
                homeDashEvent(
                  HOME_DASH_EVENTS.CONTAINER_EFFICIENCY_POPOVER_OPEN,
                  {
                    ...homeDashOrgPayload(org),
                    variant: "app" satisfies ContainerEfficiencyVariant,
                    itemCount: appByOrg.length,
                  },
                );
              }}
              trigger={
                <div
                  className="flex items-center gap-3 cursor-pointer w-full text-left min-w-0"
                  aria-label="App Service container efficiency: view breakdown and recommendations"
                >
                  <div className="flex items-center gap-1.5 text-gray-600 w-36 flex-shrink-0">
                    <IconBox variant="sm" className="flex-shrink-0" />
                    <span className="text-sm font-medium">App Service</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <SegmentedBar
                      segments={getBarSegments(appByOrg, "app")}
                      animate
                    />
                  </div>
                </div>
              }
              placement="top"
            >
              <EfficiencyTooltipContent
                variant="app"
                under={appTooltipData.under}
                over={appTooltipData.over}
                totalItems={appByOrg.length}
                org={org}
              />
            </Popover>
          )}
          {showSkeleton ? (
            <div className="flex items-center gap-3 w-full">
              <div className="flex items-center gap-1.5 text-gray-600 w-36 flex-shrink-0">
                <IconCylinder variant="sm" className="flex-shrink-0" />
                <span className="text-sm font-medium">Database</span>
              </div>
              <div className="flex-1 min-w-0 h-5 rounded bg-gray-200 animate-pulse" />
            </div>
          ) : dbByOrg.length === 0 ? (
            <div className="flex items-center gap-3 w-full">
              <div className="flex items-center gap-1.5 text-gray-600 w-36 flex-shrink-0">
                <IconCylinder variant="sm" className="flex-shrink-0" />
                <span className="text-sm font-medium">Database</span>
              </div>
              <div className="flex-1 min-w-0 text-sm text-gray-600">
                No databases.{" "}
                <Link
                  to={createDbUrl()}
                  className="underline"
                  onClick={() =>
                    homeDashEvent(
                      HOME_DASH_EVENTS.CONTAINER_EFFICIENCY_EMPTY_STATE_CTA,
                      {
                        ...homeDashOrgPayload(org),
                        target: "create_database",
                      },
                    )
                  }
                >
                  Provision a database →
                </Link>
              </div>
            </div>
          ) : (
            <Popover
              onOpenChange={(isOpen) => {
                if (!isOpen) return;
                homeDashEvent(
                  HOME_DASH_EVENTS.CONTAINER_EFFICIENCY_POPOVER_OPEN,
                  {
                    ...homeDashOrgPayload(org),
                    variant: "database" satisfies ContainerEfficiencyVariant,
                    itemCount: dbByOrg.length,
                  },
                );
              }}
              trigger={
                <div
                  className="flex items-center gap-3 cursor-pointer w-full text-left min-w-0"
                  aria-label="Database container efficiency: view breakdown and recommendations"
                >
                  <div className="flex items-center gap-1.5 text-gray-600 w-36 flex-shrink-0">
                    <IconCylinder variant="sm" className="flex-shrink-0" />
                    <span className="text-sm font-medium">Database</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <SegmentedBar
                      segments={getBarSegments(dbByOrg, "database")}
                      animate
                    />
                  </div>
                </div>
              }
              placement="top"
            >
              <EfficiencyTooltipContent
                variant="database"
                under={dbTooltipData.under}
                over={dbTooltipData.over}
                totalItems={dbByOrg.length}
                org={org}
              />
            </Popover>
          )}
          {showSharedLegend ? (
            <div className="flex gap-3">
              <div className="w-36 flex-shrink-0" aria-hidden />
              <div className="flex-1 min-w-0">
                <SharedEfficiencyLegend />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
